// retention-analytics-reporting.js - Tier 2 Data & Research
export const content = {
  heroStyle: 'gradient',
  tag: 'Analytics',

  html: `
<p>Numbers tell stories. Retention analytics tell you what's working, what's not, where your problems are, and whether you're improving. Without analytics, you're guessing. With analytics, you're making informed decisions.</p>

<p>This guide covers what to measure, how to analyze it, and how to turn data into action.</p>

<h2>Core Retention Metrics</h2>

<h3>Overall Retention Rate</h3>
<p><strong>Calculation:</strong> (Clients at end who were also at start) / (Clients at start) × 100</p>

<p>This is your headline metric. It tells you what percentage of clients you're keeping over a given period (typically 12 months).</p>

<ul>
<li>Below 75%: Significant retention problem</li>
<li>75-85%: Average range</li>
<li>85-90%: Above average</li>
<li>90%+: Excellent</li>
</ul>

<h3>First-Year Retention</h3>
<p>Same calculation, but only for clients enrolled in the past year. This isolates the highest-risk population and shows how well your onboarding works.</p>

<p>Target: 85%+ (given that industry average is ~84-85%)</p>

<h3>Churn Rate</h3>
<p>The inverse of retention: what percentage of clients you're losing.</p>
<p><strong>Calculation:</strong> (Clients lost) / (Clients at start) × 100</p>

<h3>Net Client Growth</h3>
<p><strong>Calculation:</strong> New clients - Lost clients</p>
<p>Are you growing or shrinking? Even with new enrollments, you may be net-negative if churn is high.</p>

<h2>Segment Analysis Metrics</h2>

<h3>Retention by Plan Type</h3>
<ul>
<li>MA retention rate</li>
<li>Med Supp retention rate</li>
<li>PDP retention rate</li>
</ul>
<p>Different products have different retention dynamics. Track separately.</p>

<h3>Retention by Specific Plan</h3>
<ul>
<li>Which plans retain best?</li>
<li>Which plans have chronic churn problems?</li>
</ul>
<p>If Plan X consistently churns at 25% while Plan Y churns at 10%, that's actionable intelligence.</p>

<h3>Retention by Population</h3>
<ul>
<li>Dual eligible retention</li>
<li>SNP retention</li>
<li>Non-dual retention</li>
</ul>
<p>Different populations have different baseline expectations.</p>

<h3>Retention by Tenure</h3>
<ul>
<li>First-year clients</li>
<li>2-5 year clients</li>
<li>5+ year clients</li>
</ul>
<p>Tenure analysis shows where in the client lifecycle you're losing people.</p>

<h2>Activity Metrics</h2>

<h3>Touchpoint Completion Rate</h3>
<p><strong>Calculation:</strong> (Touchpoints completed) / (Touchpoints scheduled) × 100</p>

<p>Are your retention activities actually happening?</p>

<h3>Contact Recency</h3>
<ul>
<li>Average days since last contact</li>
<li>Percentage contacted in last 90 days</li>
<li>Percentage with no contact in 180+ days</li>
</ul>
<p>Leading indicator: clients with long contact gaps are higher risk.</p>

<h3>Annual Review Completion</h3>
<p>What percentage of your book received an annual review?</p>
<p>Target: 90%+ completion</p>

<h3>Response Rates</h3>
<p>When you reach out, do clients respond?</p>
<ul>
<li>Email open rates</li>
<li>Call answer rates</li>
<li>Text response rates</li>
</ul>

<h2>Risk Distribution Analysis</h2>

<h3>Risk Score Distribution</h3>
<p>What percentage of your book is in each risk tier?</p>
<ul>
<li>Critical (80-100)</li>
<li>High (60-79)</li>
<li>Moderate (40-59)</li>
<li>Low (0-39)</li>
</ul>
<p>Healthy distribution: majority in low/moderate. Too many in critical/high = systemic issues.</p>

<h3>Risk Score Trends</h3>
<ul>
<li>How many clients moved to higher risk this month?</li>
<li>How many moved to lower risk?</li>
<li>What's causing the shifts?</li>
</ul>

<h2>Outcome Analysis</h2>

<h3>Churn Reason Tracking</h3>
<p>For clients who left, why?</p>
<ul>
<li>Plan changes</li>
<li>Cost concerns</li>
<li>Service issues</li>
<li>Life changes (moved, etc.)</li>
<li>Competitor switch</li>
<li>Unknown</li>
</ul>
<p>Pattern recognition helps prevent future churn.</p>

<h3>Intervention Success Rate</h3>
<p>When you identified at-risk clients and intervened, what percentage stayed?</p>
<p>This measures whether your retention efforts actually work.</p>

<h3>Commission Impact</h3>
<ul>
<li>Commission lost to churn</li>
<li>Commission protected through retention</li>
<li>ROI of retention activities</li>
</ul>

<h2>Building Reports</h2>

<h3>Daily Report</h3>
<ul>
<li>New alerts</li>
<li>Priority action list</li>
<li>Touchpoints due today</li>
</ul>

<h3>Weekly Report</h3>
<ul>
<li>Activity completion (touchpoints, reviews)</li>
<li>Risk score changes</li>
<li>New clients / lost clients</li>
</ul>

<h3>Monthly Report</h3>
<ul>
<li>Overall retention rate</li>
<li>Segment retention breakdown</li>
<li>Activity metrics summary</li>
<li>Churn reason analysis</li>
<li>Trend comparison (month-over-month, year-over-year)</li>
</ul>

<h3>Quarterly Report</h3>
<ul>
<li>Deep segment analysis</li>
<li>Plan performance review</li>
<li>Goal progress assessment</li>
<li>Strategic recommendations</li>
</ul>

<h2>Turning Analytics Into Action</h2>

<h3>Identify Patterns</h3>
<p>What does the data tell you?</p>
<ul>
<li>Is one plan consistently underperforming?</li>
<li>Is first-year retention the problem, or established clients?</li>
<li>Are touchpoints happening but not working?</li>
</ul>

<h3>Prioritize Changes</h3>
<p>Focus on highest-impact improvements:</p>
<ul>
<li>If first-year retention is weak → improve onboarding</li>
<li>If one plan is churning → investigate and potentially shift enrollments</li>
<li>If touchpoints aren't happening → fix process/accountability</li>
</ul>

<h3>Set Targets</h3>
<p>Use benchmarks to set improvement goals:</p>
<ul>
<li>If at 78%, target 83% in 12 months</li>
<li>If first-year is 80%, target 85%</li>
<li>If touchpoint completion is 60%, target 85%</li>
</ul>

<h3>Track Progress</h3>
<p>Monitor metrics over time to see if changes are working. Adjust strategies based on results.</p>
  `,

  faqs: [
    {
      question: "What's the most important retention metric to track?",
      answer: "Overall retention rate is the headline metric—percentage of clients retained over 12 months. But don't stop there: track first-year retention (highest-risk segment), retention by segment, and activity metrics (touchpoint completion). Each tells a different part of the story."
    },
    {
      question: "How do I calculate my retention rate?",
      answer: "Retention rate = (Clients at end who were also at start) / (Clients at start) × 100. For a 12-month period: if you started with 200 clients and 170 are still with you at year end, retention is 85%. Don't count new enrollees in either number."
    },
    {
      question: "What should I track about retention activities?",
      answer: "Touchpoint completion rate (scheduled vs. completed), contact recency (days since last contact, percentage with long gaps), annual review completion rate, and response rates (do clients engage with your outreach?). These activity metrics are leading indicators of retention."
    },
    {
      question: "How often should I review retention analytics?",
      answer: "Daily: alerts and action list. Weekly: activity completion and risk changes. Monthly: overall retention rate, segment breakdown, trends. Quarterly: deep analysis, plan performance, strategic recommendations. Regular review catches problems early."
    },
    {
      question: "How do I turn analytics into action?",
      answer: "Identify patterns (which segments underperform?), prioritize highest-impact changes, set specific improvement targets with timelines, and track progress. If first-year retention is weak, focus on onboarding. If one plan churns heavily, investigate and potentially adjust enrollment strategy."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Churn Statistics",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "KFF",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `Pro IRP provides comprehensive retention analytics: segment analysis, activity tracking, risk distribution, and trend reporting. See exactly where you stand and track progress toward your retention goals.`
};

export default content;
